import { CSSProperties, PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState } from "react";
import { IScenarioNode, ScenarioNodeType } from "../../models/ILiveChatScenario";
import { ACTION_META, TYPE_META, descendantIds, sortNodes, subtreeHeight } from "../../utils/scenarioUtils";

export type DropZone = "before" | "after" | "inside" | "root";
export interface IDropTarget {
  /** "root" bölgesinde null */
  id: string | null;
  zone: DropZone;
}

type DragSource = { kind: "node"; node: IScenarioNode } | { kind: "new"; nodeType: ScenarioNodeType };

interface DragState {
  source: DragSource;
  x: number;
  y: number;
  target: IDropTarget | null;
  valid: boolean;
}

interface Props {
  tree: IScenarioNode[];
  selectedId: string | null;
  maxDepth: number;
  busyIds: Set<string>;
  typeLabels: Partial<Record<ScenarioNodeType, string>>;
  onSelect: (node: IScenarioNode) => void;
  onAdd: (parentId: string | null, nodeType?: ScenarioNodeType) => void;
  onToggleActive: (node: IScenarioNode) => void;
  onMoveStep: (node: IScenarioNode, direction: -1 | 1) => void;
  onDelete: (node: IScenarioNode) => void;
  onDropNode: (node: IScenarioNode, target: IDropTarget) => void;
  onDropNew: (nodeType: ScenarioNodeType, target: IDropTarget) => void;
}

interface NodeInfo {
  node: IScenarioNode;
  depth: number;
  parentId: string | null;
}

const DRAG_THRESHOLD = 5;
const PALETTE: ScenarioNodeType[] = ["Button", "Text", "Otp", "Form", "Ticket"];

/** Backend kuralı: kök = 0, en derin yaprak maxDepth'ten küçük olmalı. */
export const fitsDepth = (newDepth: number, height: number, maxDepth: number) => newDepth + height < maxDepth;

export default function ScenarioTree({
  tree, selectedId, maxDepth, busyIds, typeLabels,
  onSelect, onAdd, onToggleActive, onMoveStep, onDelete, onDropNode, onDropNew,
}: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const pending = useRef<{ source: DragSource; startX: number; startY: number; active: boolean } | null>(null);
  const suppressClick = useRef(false);

  // id → derinlik / üst adım
  const info = useMemo(() => {
    const map = new Map<string, NodeInfo>();
    const walk = (nodes: IScenarioNode[], depth: number, parentId: string | null) =>
      nodes.forEach((n) => {
        map.set(n.id, { node: n, depth, parentId });
        walk(n.children ?? [], depth + 1, n.id);
      });
    walk(tree, 0, null);
    return map;
  }, [tree]);

  // Arama: eşleşen adımlar ve üstleri görünür
  const visibleIds = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return null;
    const ids = new Set<string>();
    const walk = (n: IScenarioNode): boolean => {
      const self = [n.text, n.content, n.code].some((s) => (s ?? "").toLocaleLowerCase("tr").includes(q));
      const child = (n.children ?? []).map(walk).some(Boolean);
      if (self || child) ids.add(n.id);
      return self || child;
    };
    tree.forEach(walk);
    return ids;
  }, [tree, search]);

  // Menü dışına tıklayınca kapat
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.(".lcs-menu, .lcs-row-kebab")) setMenuFor(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuFor]);

  /* ----------------------------- Sürükle-bırak ----------------------------- */

  const validate = (source: DragSource, target: IDropTarget | null): boolean => {
    if (!target) return false;
    let newDepth = 0;
    if (target.zone !== "root") {
      const t = target.id ? info.get(target.id) : null;
      if (!t) return false;
      newDepth = target.zone === "inside" ? t.depth + 1 : t.depth;
    }
    if (source.kind === "new") return fitsDepth(newDepth, 0, maxDepth);
    if (target.id === source.node.id) return false;
    if (target.id && descendantIds(source.node).has(target.id)) return false;
    return fitsDepth(newDepth, subtreeHeight(source.node), maxDepth);
  };

  // Pencere dinleyicileri en güncel props'u görebilsin
  const latest = useRef({ validate, onDropNode, onDropNew, onAdd, selectedId });
  latest.current = { validate, onDropNode, onDropNew, onAdd, selectedId };

  const hitTest = (x: number, y: number): IDropTarget | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const zoneEl = el?.closest<HTMLElement>("[data-drop]");
    if (!zoneEl) return null;
    if (zoneEl.dataset.drop === "root") return { id: null, zone: "root" };
    const rect = zoneEl.getBoundingClientRect();
    const ratio = (y - rect.top) / rect.height;
    const zone: DropZone = ratio < 0.28 ? "before" : ratio > 0.72 ? "after" : "inside";
    return { id: zoneEl.dataset.nodeId ?? null, zone };
  };

  const autoScroll = (y: number) => {
    const box = scrollRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const edge = 48;
    if (y < rect.top + edge) box.scrollTop -= 12;
    else if (y > rect.bottom - edge) box.scrollTop += 12;
    // Mobilde ağaç sayfayla kayar
    if (y < 60) window.scrollBy(0, -12);
    else if (y > window.innerHeight - 60) window.scrollBy(0, 12);
  };

  const onPointerMove = (e: PointerEvent) => {
    const p = pending.current;
    if (!p) return;
    if (!p.active) {
      if (Math.hypot(e.clientX - p.startX, e.clientY - p.startY) < DRAG_THRESHOLD) return;
      p.active = true;
      document.body.classList.add("lcs-dragging");
      setMenuFor(null);
    }
    e.preventDefault();
    autoScroll(e.clientY);
    const target = hitTest(e.clientX, e.clientY);
    setDrag({ source: p.source, x: e.clientX, y: e.clientY, target, valid: latest.current.validate(p.source, target) });
  };

  const endDrag = (e: PointerEvent, cancelled: boolean) => {
    const p = pending.current;
    pending.current = null;
    document.body.classList.remove("lcs-dragging");
    setDrag(null);
    if (!p) return;

    if (!p.active) {
      // Sürüklenmeden bırakılan palet öğesi = tıklama: seçili adımın altına ekle
      if (!cancelled && p.source.kind === "new") latest.current.onAdd(latest.current.selectedId, p.source.nodeType);
      return;
    }
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 0);
    if (cancelled) return;

    const target = hitTest(e.clientX, e.clientY);
    if (!target || !latest.current.validate(p.source, target)) return;
    if (p.source.kind === "node") latest.current.onDropNode(p.source.node, target);
    else latest.current.onDropNew(p.source.nodeType, target);
  };

  const cleanupRef = useRef<(() => void) | null>(null);

  const beginDrag = (e: ReactPointerEvent, source: DragSource) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    cleanupRef.current?.();
    pending.current = { source, startX: e.clientX, startY: e.clientY, active: false };

    const move = (ev: PointerEvent) => onPointerMove(ev);
    const finish = (ev: PointerEvent, cancelled: boolean) => { cleanup(); endDrag(ev, cancelled); };
    const up = (ev: PointerEvent) => finish(ev, false);
    const cancel = (ev: PointerEvent) => finish(ev, true);
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      cleanupRef.current = null;
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    cleanupRef.current = cleanup;
  };

  useEffect(() => () => {
    cleanupRef.current?.();
    document.body.classList.remove("lcs-dragging");
  }, []);

  /* -------------------------------- Görünüm -------------------------------- */

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allParentIds = useMemo(() => Array.from(info.values()).filter((i) => (i.node.children ?? []).length).map((i) => i.node.id), [info]);

  const dropClass = (id: string) => {
    if (!drag?.target || drag.target.id !== id) return "";
    if (!drag.valid) return " lcs-row--drop-invalid";
    return ` lcs-row--drop-${drag.target.zone}`;
  };

  const renderNodes = (nodes: IScenarioNode[], depth: number, parentHidden: boolean): JSX.Element[] =>
    sortNodes(nodes)
      .filter((n) => !visibleIds || visibleIds.has(n.id))
      .flatMap((n, index, list) => {
        const kids = n.children ?? [];
        const hidden = parentHidden || !n.isActive;
        const isCollapsed = !visibleIds && collapsed.has(n.id);
        const type = TYPE_META[n.nodeType] ?? TYPE_META.Button;
        const action = ACTION_META[n.action];
        const busy = busyIds.has(n.id);
        const canAddChild = fitsDepth(depth + 1, 0, maxDepth);
        const isSource = drag?.source.kind === "node" && drag.source.node.id === n.id;

        const row = (
          <div
            key={n.id}
            data-drop="node"
            data-node-id={n.id}
            className={`lcs-row${selectedId === n.id ? " lcs-row--selected" : ""}${hidden ? " lcs-row--hidden" : ""}${isSource ? " lcs-row--drag-source" : ""}${dropClass(n.id)}`}
            style={{ "--d": depth } as CSSProperties}
            onClick={() => { if (!suppressClick.current) onSelect(n); }}
          >
            {Array.from({ length: depth }, (_, i) => (
              <span key={i} className="lcs-row-guide" style={{ left: `calc(18px + ${i} * var(--indent))` }} />
            ))}

            <button type="button" className="lcs-grip" title="Sürükleyerek taşı"
              onPointerDown={(e) => beginDrag(e, { kind: "node", node: n })}
              onClick={(e) => e.stopPropagation()}>
              <i className="bi bi-grip-vertical"></i>
            </button>

            <div className="lcs-row-main">
              {kids.length > 0 ? (
                <button type="button" className="lcs-chevron" aria-label={isCollapsed ? "Aç" : "Kapat"}
                  onClick={(e) => { e.stopPropagation(); toggleCollapse(n.id); }}>
                  <i className={`bi ${isCollapsed ? "bi-chevron-right" : "bi-chevron-down"}`}></i>
                </button>
              ) : <span style={{ width: 20, flexShrink: 0 }} />}

              <span className="lcs-type-icon" style={{ background: type.bg, color: type.color }} title={typeLabels[n.nodeType] ?? type.label}>
                <i className={`bi ${type.icon}`}></i>
              </span>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="lcs-row-title">{n.text}</div>
                <div className="lcs-row-meta">
                  <span className="lcs-badge" style={{ background: type.bg, color: type.color }}>{typeLabels[n.nodeType] ?? type.label}</span>
                  {action && (
                    <span className="lcs-badge" style={{ background: action.bg, color: action.color }}>
                      <i className={`bi ${action.icon}`}></i>{action.label}
                    </span>
                  )}
                  {kids.length > 0 && isCollapsed && (
                    <span className="lcs-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{kids.length} alt adım</span>
                  )}
                  {!n.isActive ? (
                    <span className="lcs-badge" style={{ background: "#f3f4f6", color: "#6b7280" }}><i className="bi bi-pause-fill"></i>Pasif</span>
                  ) : parentHidden ? (
                    <span className="lcs-badge" style={{ background: "#f3f4f6", color: "#6b7280" }} title="Üst adımlardan biri pasif olduğu için müşteriye görünmüyor">
                      <i className="bi bi-eye-slash"></i>Gizli
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <button type="button" disabled={busy}
              className={`lcs-switch ${n.isActive ? "lcs-switch--on" : "lcs-switch--off"}`}
              title={n.isActive ? "Aktif — pasife al" : "Pasif — aktif et"}
              aria-label={n.isActive ? "Pasife al" : "Aktif et"}
              onClick={(e) => { e.stopPropagation(); onToggleActive(n); }} />

            {/* Masaüstü: satır üstüne gelince görünen işlemler */}
            <div className="lcs-row-actions" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="lcs-icon-btn" title="Yukarı taşı" disabled={index === 0 || busy} onClick={() => onMoveStep(n, -1)}>
                <i className="bi bi-arrow-up"></i>
              </button>
              <button type="button" className="lcs-icon-btn" title="Aşağı taşı" disabled={index === list.length - 1 || busy} onClick={() => onMoveStep(n, 1)}>
                <i className="bi bi-arrow-down"></i>
              </button>
              <button type="button" className="lcs-icon-btn" title={canAddChild ? "Alt adım ekle" : "Derinlik sınırına ulaşıldı"} disabled={!canAddChild} onClick={() => onAdd(n.id)}>
                <i className="bi bi-plus-lg"></i>
              </button>
              <button type="button" className="lcs-icon-btn lcs-icon-btn--danger" title="Sil" onClick={() => onDelete(n)}>
                <i className="bi bi-trash3"></i>
              </button>
            </div>

            {/* Mobil: menü */}
            <button type="button" className="lcs-icon-btn lcs-row-kebab" aria-label="İşlemler"
              onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === n.id ? null : n.id); }}>
              <i className="bi bi-three-dots-vertical"></i>
            </button>
            {menuFor === n.id && (
              <div className="lcs-menu" onClick={(e) => { e.stopPropagation(); setMenuFor(null); }}>
                <button type="button" onClick={() => onSelect(n)}><i className="bi bi-pencil"></i>Düzenle</button>
                <button type="button" disabled={!canAddChild} onClick={() => onAdd(n.id)}><i className="bi bi-plus-lg"></i>Alt adım ekle</button>
                <button type="button" disabled={index === 0} onClick={() => onMoveStep(n, -1)}><i className="bi bi-arrow-up"></i>Yukarı taşı</button>
                <button type="button" disabled={index === list.length - 1} onClick={() => onMoveStep(n, 1)}><i className="bi bi-arrow-down"></i>Aşağı taşı</button>
                <button type="button" className="lcs-menu-danger" onClick={() => onDelete(n)}><i className="bi bi-trash3"></i>Sil</button>
              </div>
            )}
          </div>
        );

        return isCollapsed || !kids.length ? [row] : [row, ...renderNodes(kids, depth + 1, hidden)];
      });

  const rows = renderNodes(tree, 0, false);
  const rootOver = drag?.target?.zone === "root";
  const ghostType = drag ? TYPE_META[drag.source.kind === "node" ? drag.source.node.nodeType : drag.source.nodeType] : null;

  let hint = "";
  if (drag?.target) {
    if (!drag.valid) hint = "Buraya bırakılamaz";
    else if (drag.target.zone === "root") hint = "Açılış butonu olur";
    else if (drag.target.zone === "inside") hint = "Alt adımı olur";
    else hint = drag.target.zone === "before" ? "Üstüne yerleşir" : "Altına yerleşir";
  }

  return (
    <div className="lcs-card">
      {/* Adım paleti */}
      <div className="lcs-palette">
        {PALETTE.map((t) => {
          const meta = TYPE_META[t];
          return (
            <div key={t} className="lcs-palette-item" role="button" tabIndex={0}
              title="Ağaçtaki bir adımın üzerine sürükleyin ya da tıklayın"
              onPointerDown={(e) => beginDrag(e, { kind: "new", nodeType: t })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAdd(selectedId, t); } }}>
              <span className="lcs-type-icon" style={{ width: 24, height: 24, borderRadius: 7, fontSize: ".72rem", background: meta.bg, color: meta.color }}>
                <i className={`bi ${meta.icon}`}></i>
              </span>
              {typeLabels[t] ?? meta.label}
            </div>
          );
        })}
      </div>

      {/* Arama + aç/kapa */}
      <div style={{ display: "flex", gap: 6, padding: "10px 12px 4px", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }}>
          <i className="bi bi-search" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: ".8rem" }}></i>
          <input className="lcs-input" style={{ paddingLeft: 32, paddingTop: 7, paddingBottom: 7 }} value={search}
            onChange={(e) => setSearch(e.target.value)} placeholder="Adım ara..." />
        </div>
        <button type="button" className="lcs-icon-btn" title="Hepsini aç" onClick={() => setCollapsed(new Set())}>
          <i className="bi bi-arrows-expand"></i>
        </button>
        <button type="button" className="lcs-icon-btn" title="Hepsini kapat" onClick={() => setCollapsed(new Set(allParentIds))}>
          <i className="bi bi-arrows-collapse"></i>
        </button>
      </div>

      <div className="lcs-tree" ref={scrollRef}>
        {rows.length === 0 ? (
          <div className="lcs-empty" style={{ padding: "36px 12px" }}>
            <i className="bi bi-diagram-3" style={{ fontSize: "2rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
            <span style={{ fontSize: ".85rem" }}>{search ? "Aramaya uyan adım yok." : "Henüz senaryo adımı yok."}</span>
          </div>
        ) : rows}

        {drag ? (
          <div data-drop="root" className={`lcs-root-drop${rootOver ? (drag.valid ? " lcs-root-drop--over" : " lcs-root-drop--invalid") : ""}`}>
            <i className="bi bi-box-arrow-in-down me-1"></i>Buraya bırak — açılış butonu olsun
          </div>
        ) : (
          <button type="button" className="lcs-btn lcs-btn--ghost" style={{ width: "100%", marginTop: 6, color: "#4f46e5", borderStyle: "dashed", borderColor: "#c7d2fe" }} onClick={() => onAdd(null)}>
            <i className="bi bi-plus-circle"></i>Açılış butonu ekle
          </button>
        )}
      </div>

      {drag && ghostType && (
        <>
          <div className="lcs-ghost" style={{ left: drag.x + 14, top: drag.y + 10 }}>
            <span className="lcs-type-icon" style={{ width: 24, height: 24, borderRadius: 7, fontSize: ".72rem", background: ghostType.bg, color: ghostType.color }}>
              <i className={`bi ${ghostType.icon}`}></i>
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {drag.source.kind === "node" ? drag.source.node.text : `Yeni ${(typeLabels[drag.source.nodeType] ?? ghostType.label).toLocaleLowerCase("tr")}`}
            </span>
          </div>
          {hint && (
            <div className="lcs-ghost-hint" style={{ left: drag.x + 14, top: drag.y + 48, background: drag.valid ? "#4f46e5" : "#dc2626" }}>{hint}</div>
          )}
        </>
      )}
    </div>
  );
}
